import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import projectService from '../services/projectService';

/**
 * Joining a project with a code.
 *
 * The code is resolved and shown before anything happens. Joining a shared
 * workspace is a real act, and seeing what it is before confirming costs one
 * click and removes all of the doubt.
 */

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 2rem;
`;

const Panel = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.85rem 1rem;
  color: var(--text-primary);
  font-family: var(--font-mono, monospace);
  font-size: 1.05rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &:focus { outline: none; border-color: var(--accent-cyan); }
`;

const Button = styled.button`
  width: 100%;
  margin-top: 1rem;
  background: var(--accent-cyan);
  color: var(--primary-bg);
  border: none;
  border-radius: var(--radius-md);
  padding: 0.8rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Secondary = styled(Button)`
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
`;

const Confirm = styled.div`
  border: 1px solid rgba(34, 211, 238, 0.4);
  background: rgba(34, 211, 238, 0.06);
  border-radius: var(--radius-md);
  padding: 1.1rem 1.2rem;
  margin-top: 1.1rem;
  line-height: 1.6;
  color: var(--text-primary);
`;

const Problem = styled.div`
  border: 1px solid rgba(251, 191, 36, 0.4);
  background: rgba(251, 191, 36, 0.07);
  border-radius: var(--radius-md);
  padding: 1rem 1.2rem;
  margin-top: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.55;
`;

const ROLE_LABEL = { owner: 'Owner', approver: 'Can approve', viewer: 'Read only' };

function JoinProject() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    try {
      setBusy(true);
      setPreview(await projectService.previewJoin(code));
    } catch {
      setPreview({ ok: false, message: 'Could not read that code. Try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    try {
      setBusy(true);
      const result = await projectService.join(code);
      if (!result.ok) { setPreview(result); return; }

      projectService.setCurrent(result.projectId);
      // A full load so every page picks up the project that is now current.
      window.location.assign('/dashboard');
    } finally {
      setBusy(false);
    }
  };

  const switchToIt = () => {
    projectService.setCurrent(preview.projectId);
    window.location.assign('/dashboard');
  };

  return (
    <Container>
      <Title>Join a project</Title>
      <Subtitle>
        Someone will have given you a code that looks like ORION-7K4M-QP2X. Type it
        however it reaches you — spaces and dashes do not matter.
      </Subtitle>

      <Panel>
        <Label htmlFor="code">Invite code</Label>
        <Input
          id="code"
          value={code}
          placeholder="ORION-7K4M-QP2X"
          onChange={(e) => { setCode(e.target.value); setPreview(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) check(); }}
        />

        {!preview && (
          <Button onClick={check} disabled={busy || !code.trim()}>
            {busy ? 'Checking…' : 'Check this code'}
          </Button>
        )}

        {preview && preview.ok && (
          <>
            <Confirm>
              You are joining <strong>{preview.projectName}</strong> as{' '}
              <strong>{ROLE_LABEL[preview.role] || preview.role}</strong>.
            </Confirm>
            <Button onClick={join} disabled={busy}>
              {busy ? 'Joining…' : `Join ${preview.projectName}`}
            </Button>
          </>
        )}

        {preview && !preview.ok && (
          <>
            <Problem>{preview.message}</Problem>
            {preview.projectId
              ? <Button onClick={switchToIt}>Switch to {preview.projectName}</Button>
              : <Secondary onClick={() => { setPreview(null); setCode(''); }}>Try another code</Secondary>}
          </>
        )}

        <Secondary onClick={() => navigate('/dashboard')} style={{ marginTop: '0.6rem' }}>
          Back
        </Secondary>
      </Panel>
    </Container>
  );
}

export default JoinProject;
